import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import DomainRouter from "@/components/DomainRouter";
import { getTenantSlug } from "@/lib/tenant";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import VendorLayout from "@/pages/vendor/VendorLayout";
import Overview from "@/pages/vendor/Overview";
import Products from "@/pages/vendor/Products";
import Orders from "@/pages/vendor/Orders";
import PosPage from "@/pages/vendor/pos/PosPage";
import Customers from "@/pages/vendor/Customers";
import CustomerDetail from "@/pages/vendor/CustomerDetail";
import AccountAuth from "@/pages/storefront/account/AccountAuth";
import AccountLayout from "@/pages/storefront/account/AccountLayout";
import AccountOrders from "@/pages/storefront/account/AccountOrders";
import AccountAddresses from "@/pages/storefront/account/AccountAddresses";
import AccountProfile from "@/pages/storefront/account/AccountProfile";
import Coupons from "@/pages/vendor/Coupons";
import Categories from "@/pages/vendor/Categories";
import Blog from "@/pages/vendor/Blog";
import Inventory from "@/pages/vendor/Inventory";
import Refunds from "@/pages/vendor/Refunds";
import AITheme from "@/pages/vendor/AITheme";
import Domain from "@/pages/vendor/Domain";
import Plan from "@/pages/vendor/Plan";
import Settings from "@/pages/vendor/Settings";
import VendorProfile from "@/pages/vendor/VendorProfile";
import Homepage from "@/pages/vendor/Homepage";
import Shop from "@/pages/storefront/Shop";
import { About, Contact, Blogs, BlogPost, PolicyPage } from "@/pages/storefront/InfoPages";
import { POLICY_LINKS } from "@/lib/storenav";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminSubscriptionDetail from "@/pages/admin/AdminSubscriptionDetail";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminThemes from "@/pages/admin/AdminThemes";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import Storefront from "@/pages/storefront/Storefront";
import StorefrontLayout from "@/pages/storefront/StorefrontLayout";
import ProductDetail from "@/pages/storefront/ProductDetail";
import Checkout from "@/pages/storefront/Checkout";
import Track from "@/pages/storefront/Track";

function Protected({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-royal-dark flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/vendor"} replace />;
  return children;
}

function App() {
  const tenant = getTenantSlug();
  const Wrap = tenant ? ({ children }) => <DomainRouter slug={tenant}>{children}</DomainRouter> : BrowserRouter;
  return (
    <div className="App">
      <AuthProvider>
        <Wrap>
          <Toaster position="top-right" richColors theme="light" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<Protected role="vendor"><Onboarding /></Protected>} />

            <Route path="/vendor/orders/new" element={<Protected role="vendor"><PosPage /></Protected>} />
            <Route path="/vendor" element={<Protected role="vendor"><VendorLayout /></Protected>}>
              <Route index element={<Overview />} />
              <Route path="products" element={<Products />} />
              <Route path="categories" element={<Categories />} />
              <Route path="blog" element={<Blog />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="orders" element={<Orders />} />
              <Route path="refunds" element={<Refunds />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="ai-theme" element={<AITheme />} />
              <Route path="domain" element={<Domain />} />
              <Route path="plan" element={<Plan />} />
              <Route path="homepage" element={<Homepage />} />
              <Route path="profile" element={<VendorProfile />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminOverview />} />
              <Route path="vendors" element={<AdminVendors />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="subscriptions/:storeId" element={<AdminSubscriptionDetail />} />
              <Route path="themes" element={<AdminThemes />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="profile" element={<AdminProfile />} />
            </Route>

            <Route path="/store/:slug" element={<StorefrontLayout />}>
              <Route index element={<Storefront />} />
              <Route path="product/:productId" element={<ProductDetail />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="track" element={<Track />} />
              <Route path="account/login" element={<AccountAuth />} />
              <Route path="account" element={<AccountLayout />}>
                <Route index element={<Navigate to="orders" replace />} />
                <Route path="orders" element={<AccountOrders />} />
                <Route path="addresses" element={<AccountAddresses />} />
                <Route path="profile" element={<AccountProfile />} />
              </Route>
              <Route path="shop" element={<Shop />} />
              <Route path="about" element={<About />} />
              <Route path="blogs" element={<Blogs />} />
              <Route path="blogs/:postSlug" element={<BlogPost />} />
              <Route path="contact" element={<Contact />} />
              {POLICY_LINKS.map(([key]) => <Route key={key} path={key} element={<PolicyPage pageKey={key} />} />)}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Wrap>
      </AuthProvider>
    </div>
  );
}

export default App;
